import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import GameImageCard from '../../components/GameImageCard';
import mqttService from '../../services/mqttService';
import { getDashboardData } from '../../services/dashboardService';
import { useNotificationUnreadCount } from '../../hooks/useNotificationUnreadCount';
import { performLogout } from '../../utils/sessionLogout';
import { getStoredUserProfile } from '../../utils/userProfileStorage';
import AppFooter from '../../components/base/AppFooter';
import LottieIconButton from '../../components/base/LottieIconButton';
import { NotificationPanel } from './NotificationList';
import { getGameCardLottie, LOTTIE_ANIMATIONS } from '../../constants/LottieConstants';
import LogoutConfirmModal from '../../modals/LogoutConfirmModal';
import { BGM_EVENTS, isBgmPaused, setPageBgmEnabled, toggleBgm } from '../../utils/bgm';
import './GameSelection.css';

const getDisplayUserName = (profile) => {
  const raw = profile?.userName || profile?.username || profile?.displayName || '';
  const name = String(raw).trim();
  return name || null;
};

const resolveImage = (imageKey) => {
  if (!imageKey) return null;
  return IMAGE_CONSTANTS[imageKey] || null;
};

const resolveRoute = (routeKey) => {
  if (!routeKey || !ROUTE_ENUM[routeKey]) return null;
  return ROUTE_ENUM[routeKey].routePath;
};

const formatDashboardDateTime = (date = new Date()) => {
  const datePart = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return { datePart, timePart };
};

/** Pick day / sunset / night icon from current hour */
const getTimeOfDayIconKey = (date = new Date()) => {
  const hour = date.getHours();
  // Night: 8 PM – 5 AM, Sunset: 5–8 PM & 5–7 AM, Day: otherwise
  if (hour >= 20 || hour < 5) return 'V2_REVAMPED_TOD_NIGHT';
  if (hour >= 17 || hour < 7) return 'V2_REVAMPED_TOD_SUNSET';
  return 'V2_REVAMPED_TOD_DAY';
};

const StoreBadge = ({ store }) => {
  const iconImage = resolveImage(store.iconImageKey);
  const labelImage = resolveImage(store.labelImageKey);
  const labelTopImage = resolveImage(store.labelTopImageKey);
  const labelBottomImage = resolveImage(store.labelBottomImageKey);
  const badgeImage = resolveImage(store.badgeImageKey);
  const isIos = store.platform === 'ios';

  return (
    <a
      className={`rap-dashboard__store-btn${isIos ? ' is-ios' : ' is-android'}`}
      href={store.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={store.label}
    >
      {iconImage && (labelImage || (labelTopImage && labelBottomImage)) ? (
        <>
          <img
            className="rap-dashboard__store-icon"
            src={iconImage}
            alt=""
            width={isIos ? 27 : 28}
            height={32}
          />
          {isIos ? (
            <span className="rap-dashboard__store-copy">
              <img
                className="rap-dashboard__store-label-top"
                src={labelTopImage}
                alt=""
                width={69}
                height={7}
              />
              <img
                className="rap-dashboard__store-label-bottom"
                src={labelBottomImage}
                alt={store.label}
                width={74}
                height={16}
              />
            </span>
          ) : (
            <img
              className="rap-dashboard__store-label"
              src={labelImage}
              alt={store.label}
              width={74}
              height={28}
            />
          )}
        </>
      ) : badgeImage ? (
        <img src={badgeImage} alt={store.label} />
      ) : (
        <span>
          <small>{isIos ? 'Download on the' : 'Get it on'}</small>
          {store.label}
        </span>
      )}
    </a>
  );
};

/** Figma 21110:28556 playing / 21110:28532 muted — dashboard BGM, left of notifications */
const BgmToggleButton = () => {
  const [paused, setPaused] = useState(isBgmPaused);

  useEffect(() => {
    const onPause = () => setPaused(true);
    const onPlay = () => setPaused(false);
    window.addEventListener(BGM_EVENTS.PAUSE, onPause);
    window.addEventListener(BGM_EVENTS.PLAY, onPlay);
    return () => {
      window.removeEventListener(BGM_EVENTS.PAUSE, onPause);
      window.removeEventListener(BGM_EVENTS.PLAY, onPlay);
    };
  }, []);

  const label = paused ? 'Play background music' : 'Stop background music';

  return (
    <div className="rap-dashboard__bgm-card">
      <div className="rap-dashboard__bgm">
        <button
          type="button"
          className={`rap-dashboard__bgm-icon${paused ? ' is-stopped' : ''}`}
          onClick={toggleBgm}
          aria-pressed={!paused}
          aria-label={label}
          title={label}
        >
          {paused ? (
            <span className="rap-dashboard__bgm-icon-mute">
              <span className="rap-dashboard__bgm-icon-mute-speaker">
                <img src={IMAGE_CONSTANTS.V2_REVAMPED_BGM_MUTE_SPEAKER} alt="" />
              </span>
              <span className="rap-dashboard__bgm-icon-mute-x">
                <img src={IMAGE_CONSTANTS.V2_REVAMPED_BGM_MUTE_X} alt="" />
              </span>
            </span>
          ) : (
            <span className="rap-dashboard__bgm-icon-crop">
              <img src={IMAGE_CONSTANTS.V2_REVAMPED_BGM_SPEAKER} alt="" />
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

const GameSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [adIndex, setAdIndex] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    setPageBgmEnabled(true);
    return () => {
      setPageBgmEnabled(false);
    };
  }, []);

  useEffect(() => {
    mqttService.disconnect();
    let mounted = true;

    getDashboardData()
      .then((data) => {
        if (mounted) setDashboard(data);
      })
      .catch((error) => {
        console.error('Failed to load dashboard data:', error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!location.state?.openRoomModal) return;
    const path =
      location.state.gameId === 'rap_fire'
        ? ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath
        : ROUTE_ENUM.RAP_HOUSIE_ROOM_TYPE.routePath;
    navigate(path, { replace: true });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!dashboard?.header?.showDateTime) return undefined;
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, [dashboard?.header?.showDateTime]);

  const adSlides = useMemo(() => {
    if (!dashboard?.advertisement?.enabled) return [];
    const slides = dashboard.advertisement.slides;
    if (Array.isArray(slides) && slides.length) {
      return slides
        .map((slide) => ({
          ...slide,
          src: resolveImage(slide.imageKey),
        }))
        .filter((slide) => slide.src);
    }
    const fallback = resolveImage(dashboard.advertisement.imageKey);
    return fallback
      ? [{ id: 'ad-fallback', src: fallback, url: dashboard.advertisement.url }]
      : [];
  }, [dashboard]);

  useEffect(() => {
    if (adSlides.length < 2) return undefined;
    const timer = setInterval(() => {
      setAdIndex((prev) => (prev + 1) % adSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [adSlides.length]);

  useEffect(() => {
    setAdIndex(0);
  }, [adSlides.length]);

  const themeStyle = useMemo(() => {
    if (!dashboard) return undefined;
    const t = dashboard.theme || {};
    return {
      '--rd-page-bg': t.pageBg || undefined,
      '--rd-surface': t.surface,
      '--rd-surface-muted': t.surfaceMuted,
      '--rd-border': t.border,
      '--rd-text': t.textPrimary,
      '--rd-text-secondary': t.textSecondary,
      '--rd-accent': t.accent,
      '--rd-accent-soft': t.accentSoft,
      '--rd-accent-hot': t.accentHot,
      '--rd-nav-active-bg': t.navActiveBg,
      '--rd-nav-active-text': t.navActiveText,
      '--rd-ad-bg': t.adBg,
      '--rd-header-line': t.headerLine,
      '--rd-header-pill-bg': t.headerPillBg,
      '--rd-header-pill-border': t.headerPillBorder,
      '--rd-header-pill-shadow': t.headerPillShadow,
    };
  }, [dashboard]);

  const handleMenuClick = (item) => {
    if (item.type === 'route') {
      const path = resolveRoute(item.routeKey);
      if (path) navigate(path);
      return;
    }
    if (item.type === 'external' && item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (item.type === 'action' && item.action === 'manage_profile') {
      return;
    }
  };

  const handleGameClick = (game) => {
    if (!game.enabled) return;
    if (game.id === 'rap_housie') {
      navigate(ROUTE_ENUM.RAP_HOUSIE_ROOM_TYPE.routePath);
      return;
    }
    if (game.id === 'rap_fire') {
      navigate(ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath);
      return;
    }
    const path = resolveRoute(game.routeKey);
    if (path) navigate(path);
  };

  const handleLogout = () => {
    setIsLogoutConfirmOpen(true);
  };

  const handleLogoutCancel = () => {
    setIsLogoutConfirmOpen(false);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutConfirmOpen(false);
    performLogout(navigate);
  };

  const showNotifications = Boolean(dashboard?.header?.showNotifications);
  const {
    unreadCount: notifyBadgeCount,
    refresh: refreshNotificationCount,
    clear: clearNotificationCount,
  } = useNotificationUnreadCount(showNotifications);

  const handleNotificationsOpen = () => {
    setIsNotificationsOpen(true);
    clearNotificationCount();
  };

  const handleNotificationsClose = () => {
    setIsNotificationsOpen(false);
    refreshNotificationCount();
  };

  useEffect(() => {
    if (!showNotifications) return undefined;
    refreshNotificationCount();
  }, [location.pathname, showNotifications, refreshNotificationCount]);

  useEffect(() => {
    if (!showNotifications) return undefined;

    const onFocus = () => refreshNotificationCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [showNotifications, refreshNotificationCount]);

  if (!dashboard) {
    return <div className="rap-dashboard" style={themeStyle} />;
  }

  const brandLogo = resolveImage(dashboard.brand?.logoImageKey);
  const clientLogo = resolveImage(dashboard.sidebar?.clientLogoImageKey);
  const qrImage = resolveImage(dashboard.appPromo?.qrImageKey);
  const sunImage =
    resolveImage(getTimeOfDayIconKey(now)) ||
    resolveImage(dashboard.header?.sunImageKey) ||
    IMAGE_CONSTANTS.V2_REVAMPED_TOD_DAY;
  const { datePart, timePart } = formatDashboardDateTime(now);
  const greeting = dashboard.brand?.greeting;
  const userDisplayName = getDisplayUserName(getStoredUserProfile());

  return (
    <div className="rap-dashboard" style={themeStyle}>
      <div className="rap-dashboard__inner">
        <div className="rap-dashboard__body">
          <aside className="rap-dashboard__col rap-dashboard__col--left">
            <div className="rap-dashboard__col-top">
              {dashboard.header?.showDateTime ? (
                <div className="rap-dashboard__datetime-card">
                  {dashboard.header?.showSun !== false && sunImage ? (
                    <img
                      className="rap-dashboard__sun"
                      src={sunImage}
                      alt=""
                      width={70}
                      height={70}
                    />
                  ) : null}
                  <div className="rap-dashboard__datetime">
                    <span className="rap-dashboard__date">{datePart}</span>
                    <span className="rap-dashboard__time">{timePart}</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rap-dashboard__sidebar">
              <div className="rap-dashboard__client-logo">
                {clientLogo ? (
                  <img
                    src={clientLogo}
                    alt={dashboard.sidebar?.clientLogoAlt || 'Rapchik'}
                  />
                ) : (
                  <span>{dashboard.sidebar?.clientLogoAlt || 'Rapchik'}</span>
                )}
              </div>

              <nav className="rap-dashboard__nav" aria-label="Dashboard">
                {(dashboard.sidebar?.menu || []).map((item) => {
                  const isActive = !!item.active;
                  const className = `rap-dashboard__nav-item${isActive ? ' is-active' : ''}`;

                  if (item.type === 'external') {
                    return (
                      <a
                        key={item.id}
                        className={className}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.label}
                      </a>
                    );
                  }

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={className}
                      onClick={() => handleMenuClick(item)}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          <main className="rap-dashboard__col rap-dashboard__col--middle">
            <div className="rap-dashboard__col-top">
              <div className="rap-dashboard__brand">
                {brandLogo ? (
                  <img
                    className="rap-dashboard__logo"
                    src={brandLogo}
                    alt={dashboard.brand?.name || 'Rapchik'}
                    width={75}
                    height={66}
                  />
                ) : null}
                {dashboard.brand?.taglineHtml ? (
                  <p
                    className="rap-dashboard__tagline"
                    dangerouslySetInnerHTML={{ __html: dashboard.brand.taglineHtml }}
                  />
                ) : (
                  <p className="rap-dashboard__tagline">
                    Khelo <strong>Dil Se</strong> Jeeto <strong>Dimaag Se!</strong>
                  </p>
                )}
              </div>
            </div>

            <div className="rap-dashboard__middle-panel">
              {greeting?.enabled !== false ? (
                <section
                  className="rap-dashboard__greeting"
                  aria-label="Welcome greeting"
                >
                  <p className="rap-dashboard__greeting-line">
                    <span>{greeting?.waveEmoji || '👋'} </span>
                    {userDisplayName ? (
                      <>
                        <span>{greeting?.prefix || 'Hi,'} </span>
                        <strong className="rap-dashboard__greeting-name">
                          {userDisplayName}!
                        </strong>
                      </>
                    ) : (
                      <strong className="rap-dashboard__greeting-name">Hi!</strong>
                    )}
                  </p>
                  <p className="rap-dashboard__greeting-line">
                    <span>{greeting?.line2Before || 'Let’s have'} </span>
                    <strong className="rap-dashboard__greeting-hot">
                      {greeting?.highlightHot || 'some fun'}
                    </strong>
                    <span> {greeting?.line2Mid || 'and'} </span>
                    <strong className="rap-dashboard__greeting-accent">
                      {greeting?.highlightAccent || 'enjoy the moment!'}
                    </strong>
                    <span> {greeting?.rockEmoji || '🤘'}</span>
                  </p>
                </section>
              ) : null}

              <section className="rap-dashboard__games" aria-label="Games">
                {(dashboard.games || []).map((game) => {
                  const image = resolveImage(game.imageKey);
                  if (!image) return null;
                  return (
                    <div key={game.id} className="rap-dashboard__game-card">
                      <GameImageCard
                        image={image}
                        animationData={getGameCardLottie(game.id)}
                        alt={game.title}
                        disabled={!game.enabled}
                        onClick={() => handleGameClick(game)}
                        style={{ width: '100%', height: 'auto' }}
                      />
                    </div>
                  );
                })}
              </section>

              {dashboard.appPromo ? (
                <section className="rap-dashboard__promo" aria-label="Get the app">
                  {qrImage ? (
                    <a
                      className="rap-dashboard__promo-qr-link"
                      href={dashboard.appPromo.qrValue || 'https://onelink.to/m6xbyw'}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open app download link"
                    >
                      <img
                        className="rap-dashboard__promo-qr"
                        src={qrImage}
                        alt="App QR code"
                        width={154}
                        height={154}
                      />
                    </a>
                  ) : null}
                  <div className="rap-dashboard__promo-copy">
                    <p className="rap-dashboard__promo-title">{dashboard.appPromo.title}</p>
                    <div className="rap-dashboard__promo-stores-block">
                      <p className="rap-dashboard__promo-label">{dashboard.appPromo.availableOnLabel}</p>
                      <div className="rap-dashboard__stores">
                        {(dashboard.appPromo.stores || []).map((store) => (
                          <StoreBadge key={store.id} store={store} />
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </main>

          <aside className="rap-dashboard__col rap-dashboard__col--right">
            <div className="rap-dashboard__col-top">
              <div className="rap-dashboard__header-actions">
                <BgmToggleButton />
                {dashboard.header?.showNotifications ? (
                  <div className="rap-dashboard__notify-card">
                    <div className="rap-dashboard__notify">
                      <LottieIconButton
                        className="rap-dashboard__notify-icon"
                        playerClassName="rap-dashboard__notify-icon-player"
                        animationData={LOTTIE_ANIMATIONS.BELL_WAVE}
                        onClick={handleNotificationsOpen}
                        label="Notifications"
                      />
                      {notifyBadgeCount > 0 ? (
                        <span className="rap-dashboard__notify-badge">
                          {String(notifyBadgeCount).padStart(2, '0')}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="rap-dashboard__logout-card">
                  <div className="rap-dashboard__logout">
                    <LottieIconButton
                      className="rap-dashboard__logout-icon"
                      playerClassName="rap-dashboard__logout-icon-player"
                      animationData={LOTTIE_ANIMATIONS.LOGOUT}
                      onClick={handleLogout}
                      label={dashboard.sidebar?.logoutLabel || 'Logout'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {adSlides.length ? (
              <div className="rap-dashboard__ad" aria-label="Advertisement">
                <div className="rap-dashboard__ad-inner">
                  {adSlides.map((slide, index) => {
                    const isActive = index === adIndex;
                    const image = (
                      <img src={slide.src} alt={dashboard.advertisement?.label || 'Advertisement'} />
                    );
                    return (
                      <div
                        key={slide.id || index}
                        className={`rap-dashboard__ad-slide${isActive ? ' is-active' : ''}`}
                      >
                        {slide.url ? (
                          <a href={slide.url} target="_blank" rel="noopener noreferrer">
                            {image}
                          </a>
                        ) : (
                          image
                        )}
                      </div>
                    );
                  })}
                </div>
                <img
                  className="rap-dashboard__ad-frame"
                  src={resolveImage('V2_REVAMPED_AD_FRAME')}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                />
                {adSlides.length > 1 ? (
                  <div className="rap-dashboard__ad-indicators" aria-label="Advertisement slides">
                    {adSlides.map((slide, index) => (
                      <button
                        key={`dot-${slide.id || index}`}
                        type="button"
                        className={`rap-dashboard__ad-dot${index === adIndex ? ' is-active' : ''}`}
                        onClick={() => setAdIndex(index)}
                        aria-label={`Show ad ${index + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </aside>
        </div>

        <footer className="rap-dashboard__footer">
          {dashboard.sponsors?.items?.length ? (
            <div className="rap-dashboard__sponsors" aria-label="Sponsors">
              <div className="rap-dashboard__sponsors-track">
                {[0, 1].map((loop) =>
                  dashboard.sponsors.items.map((sponsor) => {
                    const image = resolveImage(sponsor.imageKey);
                    const content = image ? (
                      <img src={image} alt={sponsor.alt || dashboard.sponsors.label || 'Sponsor'} />
                    ) : (
                      <span>{sponsor.alt || dashboard.sponsors.label}</span>
                    );
                    const key = `${sponsor.id}-${loop}`;

                    if (sponsor.url) {
                      return (
                        <a
                          key={key}
                          className="rap-dashboard__sponsor"
                          href={sponsor.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden={loop === 1 ? true : undefined}
                          tabIndex={loop === 1 ? -1 : undefined}
                        >
                          {content}
                        </a>
                      );
                    }

                    return (
                      <div
                        key={key}
                        className="rap-dashboard__sponsor"
                        aria-hidden={loop === 1 ? true : undefined}
                      >
                        {content}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : null}

          <AppFooter text={dashboard.brand?.copyright} />
        </footer>
      </div>

      {showNotifications ? (
        <NotificationPanel
          isOpen={isNotificationsOpen}
          onClose={handleNotificationsClose}
        />
      ) : null}

      <LogoutConfirmModal
        open={isLogoutConfirmOpen}
        onCancel={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
      />
    </div>
  );
};

export default GameSelection;
